/**
 * ReservationFilterBar - 필터바
 *
 * V2: staged → applied 패턴
 * - 필터 변경 = staging만 (조회 안 됨)
 * - "검색" 버튼 클릭 = 조회 실행
 * - "초기화" 버튼 = 모든 필터 기본값 + 조회
 */

import { Search, ArrowUpDown, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DateRangePicker,
  type DateRange,
} from "@/components/ui/date-range-picker";
import { ReservationStatusChips } from "./ReservationStatusChips";
import type {
  DatePreset,
  ReservationSortOrder,
  ReservationFilterState,
} from "@/hooks/useReservationFilters";

const PRESET_OPTIONS: { value: DatePreset; label: string }[] = [
  { value: "futureToday", label: "오늘 이후" },
  { value: "thisWeek", label: "최근 1주" },
  { value: "thisMonth", label: "최근 한달" },
  { value: "all", label: "전체" },
];

interface ReservationFilterBarProps {
  filters: ReservationFilterState;
  statusCounts?: Record<string, number>;
  onPresetChange: (preset: DatePreset) => void;
  onCustomRange: (from: Date, to: Date) => void;
  onSearchChange: (q: string) => void;
  onSortChange: (sort: ReservationSortOrder) => void;
  onStatusToggle: (status: string) => void;
  onStatusClearAll: () => void;
  onStatusSetFilters: (statuses: string[]) => void;
  onSearch: () => void;
  onReset: () => void;
}

export function ReservationFilterBar({
  filters,
  statusCounts,
  onPresetChange,
  onCustomRange,
  onSearchChange,
  onSortChange,
  onStatusToggle,
  onStatusClearAll,
  onStatusSetFilters,
  onSearch,
  onReset,
}: ReservationFilterBarProps) {
  const dateRange: DateRange = {
    from: filters.dateRange.from,
    to: filters.dateRange.to,
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    if (range?.from && range?.to) {
      onCustomRange(range.from, range.to);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onSearch();
    }
  };

  return (
    <div className="space-y-3">
      {/* Row 1: DateField Toggle + DateRangePicker + Presets + Search + Sort */}
      <div className="flex flex-col md:flex-row gap-2 items-start md:items-center">
        <div className="flex items-center gap-2 flex-wrap">
          {/* 날짜 기준: 예약일 고정 */}
          <span className="text-xs text-muted-foreground px-2">예약일</span>

          <DateRangePicker
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
            className="h-9 text-xs w-[220px]"
          />

          <div className="flex items-center bg-muted/50 rounded-lg p-0.5">
            {PRESET_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                variant={
                  filters.datePreset === opt.value ? "secondary" : "ghost"
                }
                size="sm"
                className="h-8 text-xs px-2.5"
                onClick={() => onPresetChange(opt.value)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="예약자명, 전화번호..."
              className="pl-9 w-[200px] h-9 text-xs"
              value={filters.searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>

          <Select
            value={filters.sortOrder}
            onValueChange={(v) => onSortChange(v as ReservationSortOrder)}
          >
            <SelectTrigger className="w-[140px] h-9 text-xs">
              <ArrowUpDown className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue placeholder="정렬" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="appointmentDate,asc">예약일 빠른순</SelectItem>
              <SelectItem value="appointmentDate,desc">
                예약일 늦은순
              </SelectItem>
              <SelectItem value="occurredAt,desc">신청일 최신순</SelectItem>
              <SelectItem value="occurredAt,asc">신청일 과거순</SelectItem>
            </SelectContent>
          </Select>

          {/* 검색 + 초기화 */}
          <Button size="sm" className="h-9 px-3 text-xs" onClick={onSearch}>
            <Search className="h-4 w-4 mr-1" />
            검색
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-9 px-3 text-xs"
            onClick={onReset}
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1" />
            초기화
          </Button>
        </div>
      </div>

      {/* Row 2: Status Chips */}
      <ReservationStatusChips
        activeFilters={filters.statusFilters}
        statusCounts={statusCounts}
        onToggle={onStatusToggle}
        onClearAll={onStatusClearAll}
        onSetFilters={onStatusSetFilters}
      />
    </div>
  );
}
